// Iter 84 · Phase 8 — Closeout Command Center
// Per-project unified closeout dashboard. Single screen showing every
// closeout-related surface for a project. Reuses existing endpoints.
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CheckCircle2, AlertTriangle, Package, Share2, FileText,
  Printer, Clock, ClipboardList,
} from "lucide-react";
import { api } from "@/lib/api";

export default function CloseoutCenter() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [punches, setPunches] = useState([]);
  const [exports_, setExports] = useState([]);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [p, pi, ex, sh] = await Promise.all([
          api.get(`/projects/${projectId}`).then((r) => r.data).catch(() => null),
          api.get(`/projects/${projectId}/punch-items`, { params: { limit: 500 }})
            .then((r) => r.data.items || r.data.punch_items || []).catch(() => []),
          api.get(`/projects/${projectId}/plan-exports`)
            .then((r) => r.data.items || r.data.exports || r.data || []).catch(() => []),
          api.get(`/projects/${projectId}/plan-shares`)
            .then((r) => r.data.items || r.data.shares || r.data || []).catch(() => []),
        ]);
        setProject(p);
        setPunches(pi);
        setExports(ex);
        setShares(sh);
      } finally { setLoading(false); }
    })();
  }, [projectId]);

  const open      = punches.filter((p) => p.status === "open").length;
  const inProg    = punches.filter((p) => p.status === "in_progress").length;
  const review    = punches.filter((p) => p.status === "ready_for_review").length;
  const closed    = punches.filter((p) => p.status === "closed").length;
  const rejected   = punches.filter((p) => p.status === "rejected").length;
  const totalP     = punches.length || 1;
  const completed  = Math.round((closed / totalP) * 100);

  const completedExports = exports_.filter((j) => j.status === "completed");
  const activeShares = shares.filter((s) => !s.revoked_at && (!s.expires_at || new Date(s.expires_at) > new Date()));

  return (
    <div className="min-h-screen bg-[var(--fop-bg,#0a0a0b)] text-[var(--fop-text)]" data-testid="closeout-center">
      <style>{`@media print {
        body, [data-testid="closeout-center"] { background: white !important; color: black !important; }
        [data-no-print] { display: none !important; }
      }`}</style>
      <div className="max-w-6xl mx-auto px-6 py-8">
        <header className="mb-6 flex items-start justify-between gap-4" data-no-print>
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--fop-primary)]">
              ForgedOps · Closeout Command Center
            </div>
            <h1 className="text-2xl font-semibold mt-1">{project?.name || "Project Closeout"}</h1>
            <div className="text-[12px] text-[var(--fop-text-3)] mt-1">
              {totalP - 1 + (punches.length ? 0 : 1)} punch items · {completedExports.length} export{completedExports.length === 1 ? "" : "s"} · {activeShares.length} active share{activeShares.length === 1 ? "" : "s"}
            </div>
          </div>
          <button
            onClick={() => window.print()}
            data-testid="closeout-print-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded border border-[var(--fop-primary)]/40 text-[12px] font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:bg-[var(--fop-primary)]/10 min-h-[44px]"
          >
            <Printer size={14} /> Print packet
          </button>
        </header>

        {/* Closeout % large display */}
        <div className="mb-6 px-6 py-6 rounded-md bg-[var(--fop-surface)] border-l-3 border-[var(--fop-primary)] border border-[var(--fop-border)] text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-3)]">Closeout</div>
          <div className="text-5xl font-semibold mt-2" data-testid="closeout-pct">{completed}%</div>
          <div className="text-[12px] text-[var(--fop-text-2)] mt-2">
            {closed} closed · {open + inProg + review} active
          </div>
        </div>

        {/* Status grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatusCard label="Open"           value={open}     icon={ClipboardList} tone="warn" />
          <StatusCard label="In Progress"     value={inProg}   icon={Clock}          tone="warn" />
          <StatusCard label="Review"          value={review}   icon={AlertTriangle} tone="ok" />
          <StatusCard label="Closed"          value={closed}   icon={CheckCircle2} tone="ok" />
          <StatusCard label="Rejected"        value={rejected} icon={AlertTriangle} tone="red" />
        </div>

        {/* Deliverables grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Deliverable
            label="Turnover Packages"
            count={completedExports.length}
            cta="View Exports"
            icon={Package}
            onClick={() => navigate(`/project/${projectId}/exports`)}
          />
          <Deliverable
            label="Owner Deliverables"
            count={activeShares.length}
            cta="View Shares"
            icon={Share2}
            onClick={() => navigate(`/project/${projectId}/shares`)}
          />
          <Deliverable
            label="Review Items"
            count={review}
            cta="Open Punch Center"
            icon={FileText}
            onClick={() => navigate(`/project/${projectId}/punch`)}
          />
        </div>

        {/* Open / Overdue punch summary */}
        <section className="border border-[var(--fop-border)] rounded-md bg-[var(--fop-surface)] overflow-hidden">
          <div className="px-4 py-2 border-b border-[var(--fop-border)] font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-primary)]">
            Open punch items · {open + inProg}
          </div>
          {(open + inProg) === 0 && (
            <div className="px-6 py-8 text-center text-[var(--fop-text-2)] text-sm" data-testid="closeout-no-open">
              <CheckCircle2 size={20} className="mx-auto text-emerald-500 mb-2" />
              All punch items resolved.
            </div>
          )}
          {punches.filter((p) => ["open", "in_progress"].includes(p.status)).slice(0, 10).map((p) => (
            <div
              key={p.id}
              onClick={() => navigate(`/project/${projectId}/punch/${p.id}`)}
              className="px-4 py-2 border-b border-[var(--fop-border)] last:border-b-0 cursor-pointer hover:bg-[var(--fop-surface-2)] flex items-center gap-2"
              data-testid={`closeout-open-${p.id}`}
            >
              <ClipboardList size={12} className="text-[var(--fop-text-3)]" />
              <span className="text-sm truncate flex-1">{p.title}</span>
              <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">{p.priority || "normal"}</span>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}

function StatusCard({ label, value, icon: Icon, tone }) {
  const tones = {
    warn: "text-orange-400",
    ok:    "text-emerald-500",
    red:   "text-red-400",
  };
  return (
    <div className="px-3 py-3 rounded-md bg-[var(--fop-surface)] border border-[var(--fop-border)] min-h-[80px]">
      <div className="flex items-center gap-1.5">
        <Icon size={12} className="text-[var(--fop-text-3)]" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">{label}</span>
      </div>
      <div className={`text-2xl font-semibold mt-1 ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function Deliverable({ label, count, cta, icon: Icon, onClick }) {
  return (
    <button
      onClick={onClick}
      className="text-left px-4 py-4 rounded-md bg-[var(--fop-surface)] border border-[var(--fop-border)] hover:bg-[var(--fop-surface-2)] min-h-[100px]"
      data-testid={`closeout-deliverable-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-[var(--fop-primary)]" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">{label}</span>
      </div>
      <div className="text-3xl font-semibold">{count}</div>
      <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-primary)] mt-2">{cta} →</div>
    </button>
  );
}
